/**
 * Aumento automatico da gorjeta da Lalamove (ticket #2305).
 *
 * Espelha App\Services\LalamoveGorjeta. Quem decide o valor que vai para a
 * Lalamove e o servidor; aqui a mesma conta serve para a tela mostrar, antes
 * de confirmar, quanto a corrida custa agora e ate quanto pode chegar.
 *
 * Todo percentual e sobre o valor original da corrida, nunca sobre o valor
 * ja aumentado.
 */

export const PADRAO = {
    normal: { inicial: 0, intervalo_min: 10, aumento: 5, teto: 30 },
    prioridade: { inicial: 15, intervalo_min: 5, aumento: 10, teto: 60 },
};

export const MODOS = {
    normal: { titulo: 'Normal', subtitulo: 'Sem pressa', cor: 'purple' },
    prioridade: { titulo: 'Prioridade', subtitulo: 'Entrega urgente', cor: 'orange' },
};

/** Limites aceitos pelo servidor (salvarConfiguracao). */
export const LIMITES = {
    inicial: { min: 0, max: 200, rotulo: 'Acréscimo inicial', sufixo: '%' },
    intervalo_min: { min: 1, max: 120, rotulo: 'Intervalo entre aumentos', curto: 'Intervalo', sufixo: 'min' },
    aumento: { min: 0, max: 100, rotulo: 'Cada aumento', sufixo: '%' },
    teto: { min: 0, max: 300, rotulo: 'Teto do acréscimo', sufixo: '%' },
};

const numero = (v) => {
    if (v === '' || v === null || v === undefined) return NaN;
    return Number(String(v).replace(',', '.'));
};

const centavos = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

export const brl = (valor) => `R$ ${Number(valor || 0).toFixed(2).replace('.', ',')}`;

export const pct = (valor) => `${String(Number(valor || 0)).replace('.', ',')}%`;

/** Configuracao completada com o padrao, campo a campo. */
export function normalizar(config) {
    const saida = {};
    Object.keys(PADRAO).forEach((modo) => {
        saida[modo] = {};
        Object.keys(PADRAO[modo]).forEach((campo) => {
            const v = numero(config?.[modo]?.[campo]);
            saida[modo][campo] = Number.isFinite(v) ? Math.max(0, v) : PADRAO[modo][campo];
        });
    });
    return saida;
}

export function percentual(regra, segundos) {
    let p = Number(regra.inicial) || 0;
    const intervalo = (Number(regra.intervalo_min) || 0) * 60;
    if (intervalo > 0 && segundos > 0) {
        p += Math.floor(segundos / intervalo) * (Number(regra.aumento) || 0);
    }
    return Math.min(p, Number(regra.teto) || 0);
}

/**
 * Faixa de gorjeta que a Lalamove Brasil aceita, igual para qualquer corrida.
 * Espelha LalamoveGorjeta::MINIMO_LALAMOVE / MAXIMO_LALAMOVE.
 */
export const MINIMO_LALAMOVE = 3;
export const MAXIMO_LALAMOVE = 40;

/** Gorjeta pela regra, sem os limites da Lalamove. */
export const valorBruto = (original, regra, segundos) => centavos((Number(original) || 0) * percentual(regra, segundos) / 100);

export const valorTetoBruto = (original, regra) => centavos((Number(original) || 0) * (Number(regra.teto) || 0) / 100);

/**
 * Valor da regra dentro da faixa da Lalamove: abaixo de R$ 3 vira R$ 3, acima
 * de R$ 40 fica em R$ 40. Teto abaixo do minimo = sem gorjeta.
 */
export function efetivo(bruto, tetoBruto) {
    if (!(bruto > 0) || tetoBruto < MINIMO_LALAMOVE) return 0;
    return centavos(Math.min(Math.max(bruto, MINIMO_LALAMOVE), MAXIMO_LALAMOVE));
}

/** Gorjeta que vai para a Lalamove depois de `segundos` sem motorista. */
export const valor = (original, regra, segundos) => efetivo(valorBruto(original, regra, segundos), valorTetoBruto(original, regra));

export const valorTeto = (original, regra) => {
    const bruto = valorTetoBruto(original, regra);
    return efetivo(bruto, bruto);
};

/**
 * Segundos (desde o pedido) do proximo aumento; null se nao sobe mais.
 * Com `original`, so conta degrau que muda o valor enviado.
 */
export function proximoAumentoEm(regra, segundos, original = null) {
    const intervalo = Math.round((Number(regra.intervalo_min) || 0) * 60);
    if (intervalo <= 0 || !(Number(regra.aumento) > 0)) return null;

    let marco = (Math.floor(Math.max(0, segundos) / intervalo) + 1) * intervalo;

    if (original === null) {
        return percentual(regra, segundos) >= (Number(regra.teto) || 0) ? null : marco;
    }

    const agora = valor(original, regra, segundos);
    for (let guarda = 0; guarda < 1000; guarda += 1) {
        if (valor(original, regra, marco) > agora + 0.004) return marco;
        if (percentual(regra, marco) >= (Number(regra.teto) || 0)) return null;
        marco += intervalo;
    }
    return null;
}

/**
 * Pontos em que o valor enviado muda, do pedido ao teto:
 * [{ minuto, percentual, gorjeta, total, ajuste, pulou }], com `ajuste`
 * 'minimo' ou 'maximo' quando o limite da Lalamove mudou o valor da regra.
 *
 * Limitado a `maxPontos` para caber na tela do celular, mas o ultimo ponto e
 * sempre o teto: e a pergunta que importa ("ate quanto chega?"). Quando ha
 * pontos omitidos no meio, o teto vem com `pulou: true`.
 */
export function linhaDoTempo(original, regra, maxPontos = 6) {
    const todos = [];
    let segundos = 0;

    for (let guarda = 0; guarda < 500; guarda += 1) {
        const g = valor(original, regra, segundos);
        const bruto = valorBruto(original, regra, segundos);
        let ajuste = null;
        if (g > 0 && bruto < g - 0.004) ajuste = 'minimo';
        if (g > 0 && bruto > g + 0.004) ajuste = 'maximo';
        todos.push({
            minuto: Math.round(segundos / 60),
            percentual: percentual(regra, segundos),
            gorjeta: g,
            total: centavos(Number(original) + g),
            ajuste,
            pulou: false,
        });
        const prox = proximoAumentoEm(regra, segundos, original);
        if (prox === null) break;
        segundos = prox;
    }

    if (todos.length <= maxPontos) return todos;

    return [...todos.slice(0, Math.max(1, maxPontos - 1)), { ...todos[todos.length - 1], pulou: true }];
}

/** Minutos ate a gorjeta parar de subir; null se nunca sobe. */
export function minutosAteOTeto(regra, original = 100) {
    const pontos = linhaDoTempo(original, regra, 1000);
    return pontos.length > 1 ? pontos[pontos.length - 1].minuto : null;
}

/** Frase curta da regra, para o operador entender sem abrir a configuracao. */
export function descrever(regra) {
    const partes = [];
    partes.push(Number(regra.inicial) > 0 ? `começa com +${pct(regra.inicial)}` : 'começa no preço da corrida');
    if (Number(regra.aumento) > 0 && Number(regra.teto) > Number(regra.inicial)) {
        partes.push(`sobe +${pct(regra.aumento)} a cada ${regra.intervalo_min} min sem motorista`);
        partes.push(`até +${pct(regra.teto)}`);
    } else {
        partes.push('não sobe com o tempo');
    }
    return partes.join(', ');
}

/** Problemas que impedem salvar (erros) e avisos que so merecem atencao. */
export function conferir(regras) {
    const erros = [];
    const avisos = [];

    Object.keys(PADRAO).forEach((modo) => {
        const nome = MODOS[modo].titulo;
        Object.entries(LIMITES).forEach(([campo, lim]) => {
            const v = numero(regras?.[modo]?.[campo]);
            if (!Number.isFinite(v)) {
                erros.push(`${nome}: preencha "${lim.rotulo}".`);
            } else if (v < lim.min || v > lim.max) {
                erros.push(`${nome}: "${lim.rotulo}" precisa ficar entre ${lim.min} e ${lim.max} ${lim.sufixo}.`);
            }
        });

        const r = regras?.[modo] || {};
        if (numero(r.teto) < numero(r.inicial)) {
            avisos.push(`${nome}: o teto é menor que o acréscimo inicial, então a corrida já começa no teto.`);
        }
    });

    const n = normalizar(regras);
    if (n.prioridade.inicial < n.normal.inicial || n.prioridade.teto < n.normal.teto) {
        avisos.push('A prioridade está menos agressiva que o normal em algum ponto. Confira se é isso mesmo.');
    }

    return { erros, avisos };
}
